import React, { Component } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { getUserConversationById, getMessagesByConversationId, sendConversationMessage, pushSingleMessage,
  updateUserConversationPreferences } from '../../../actions/conversations';
import { extractConversationWithMessagesById } from '../../../helpers/conversations';
import { addVisibilityListener, removeVisibilityListener,
  subscribeToVisibilityChange, unsubscribeFromVisibilityChange } from '../../../helpers/handleDocumentVisibility';
import { isTimeDifferenceMoreThanOneHour } from '../../../helpers/date';
import { supportSenderData } from '../../../constants/conversations';
import Loader from '../../common/ui/Loader';
import NotificationsService from '../../../services/NotificationsService';
import Conversation from './Conversation';
import UsersService from '../../../services/UsersService';

class ConversationContainer extends Component {
  static propTypes = {
    showHideChatIcon: PropTypes.bool,
    conversation: PropTypes.object,
    conversationId: PropTypes.number,
    messages: PropTypes.object.isRequired,
    isLoading: PropTypes.bool.isRequired,
    getUserConversationById: PropTypes.func.isRequired,
    getMessagesByConversationId: PropTypes.func.isRequired,
    sendConversationMessage: PropTypes.func.isRequired,
    pushSingleMessage: PropTypes.func.isRequired,
    updateConversationMessagesCount: PropTypes.func,
    updateUserConversationPreferences: PropTypes.func.isRequired
  };

  componentDidMount () {
    const id = this.getConversationId();
    this.props.getUserConversationById(id);
    if (UsersService.isAdmin()) {
      // There is separate page with chat for admin. So we need this additional callback for opened chat.
      NotificationsService.setCallbackOnNotificationFromChat(this.props.pushSingleMessage);
    }
    subscribeToVisibilityChange(() => this.props.getUserConversationById(id));
    addVisibilityListener();
  }

  componentWillUnmount () {
    unsubscribeFromVisibilityChange(() => this.props.getUserConversationById(this.getConversationId()));
    removeVisibilityListener();
  }

  getConversationId = () => UsersService.isAdmin() && this.props.match ? this.props.match.params.id : this.props.conversationId;

  loadMessagesHistory = () => {
    const { data } = this.props.conversation.messages;
    return this.props.getMessagesByConversationId(this.getConversationId(), data.length);
  };

  /**
   * @description
   *  Adds sender information for message groups
   * @return {{sender: Object, messages: Array}[]}
   * @private
   */
  _matchMessagesWithSender () {
    const messageGroups = this._groupMessages();
    const { conversation, user } = this.props;
    const participantsById = _.keyBy(conversation.participants, 'id');

    return messageGroups.map(function (group, index) {
      const senderId = group[0].participant_id;
      const senderData = senderId in participantsById
        ? participantsById[senderId]
        : UsersService.isAdmin() ? supportSenderData : {
          first_name: user.first_name,
          last_name: user.last_name,
          photo_image_url: user.photo_image_url
        };
      const previousMessageGroup = messageGroups[index - 1];

      return {
        sender: senderData,
        messages: group,
        showDayDelimiter: !previousMessageGroup || !moment(group[0].dc).isSame(previousMessageGroup[0].dc, 'day')
      };
    });
  }

  _groupMessages () {
    const messagesByDay = this._groupMessagesByDay();
    const gropedBySender = messagesByDay.map(this._groupMessagesBySender);

    return _.flatten(gropedBySender);
  }

  /**
   *
   * @return {Array<Array>} - each array represents messages for one day
   * @private
   */
  _groupMessagesByDay () {
    const messages = this.props.conversation.messages.data;

    const grouped = _.groupBy(messages, function (message) {
      return moment(message.dc).startOf('day');
    });

    return Object.values(grouped);
  }

  /**
   * @description
   *  Groups messages sequences by sender
   * @param {Object[]} messages
   * @return {Array<Array>}
   * @private
   * @example
   *  given [{participant_id: 1}, {participant_id: 1}, {participant_id: 2}]
   *  => [[{participant_id: 1}, {participant_id: 1}], [{participant_id: 2}]]
   */
  _groupMessagesBySender (messages) {
    const groupedMessages = [];
    let accumulator = [];

    if (messages.length) {
      for (let i = 0; i <= messages.length; i++) {
        makeGroup(i);
      }
      return groupedMessages;
    } else {
      return [];
    }

    function makeGroup (index) {
      let currentMessage = messages[index];
      let prevMessage = messages[index - 1];

      if (!currentMessage) {
        groupedMessages.push(accumulator);
        accumulator = [];

        return;
      }

      if (!accumulator.length) {
        accumulator.push(currentMessage);
      } else {
        if (prevMessage && prevMessage.participant_id === currentMessage.participant_id) {
          if (isTimeDifferenceMoreThanOneHour(currentMessage.dc, prevMessage.dc)) {
            groupedMessages.push(accumulator);

            accumulator = [currentMessage];
          } else {
            accumulator.push(currentMessage);
          }
        } else {
          groupedMessages.push(accumulator);

          accumulator = [currentMessage];
        }
      }
    }
  }

  sendMessage = (newMessage, sms, push) => {
    return this.props.sendConversationMessage(this.getConversationId(), {
      participant_id: this.props.conversation.participant_id,
      body: newMessage,
      sms,
      push
    });
  };

  pushNewAdminMessageToList = newMessage => {
    const { participant_id } = this.props.conversation;
    if (window.opener && window.opener.addAdminLastMessageToList) {
      const message = {
        body: newMessage,
        conversation_id: this.getConversationId(),
        participant_id,
        id: Date.now()
      };
      window.opener.addAdminLastMessageToList(message);
    }
  };

  readConversationMessages = () => {
    const { conversation, updateConversationMessagesCount } = this.props;
    const { conversation_id, participant_id, unread_count } = conversation;
    if (unread_count > 0) {
      if (updateConversationMessagesCount) { // for conversation screen into two columns with chat (for employer)
        updateConversationMessagesCount(conversation_id, participant_id);
      } else if (window.opener && window.opener.updateConversationMessagesCount) { // for conversation in the separate window (for admin)
        window.opener.updateConversationMessagesCount(conversation_id, participant_id);
      }
    }
  };

  render () {
    const { isLoading, conversation, showHideChatIcon, updateUserConversationPreferences } = this.props;
    if (!conversation) {
      if (isLoading) {
        return <Loader />;
      }
      if (UsersService.isEmployer()) { // when conversation was hide by employer he needs to select a chat
        return (
          <div className='conversation-screen-wrapper'>
            <h3 className='select-chat-message'>Please select a chat to start messaging</h3>
          </div>
        );
      }
      return null;
    }
    const listData = this._matchMessagesWithSender();
    return (
      <div className='conversation-screen-wrapper'>
        <Conversation
          showHideChatIcon={showHideChatIcon}
          conversation={conversation}
          listData={listData}
          sendMessage={this.sendMessage}
          loadMessagesHistory={this.loadMessagesHistory}
          pushNewAdminMessageToList={this.pushNewAdminMessageToList}
          readConversationMessages={this.readConversationMessages}
          updateUserConversationPreferences={updateUserConversationPreferences}
        />
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const conversationId = UsersService.isAdmin() && ownProps.match ? ownProps.match.params.id : ownProps.conversationId;
  const id = parseInt(conversationId, 10);
  return {
    user: state.user,
    conversation: extractConversationWithMessagesById(state, id),
    messages: state.conversationMessages,
    isLoading: state.conversations.isLoading
  };
};

const mapDispatchToProps = {
  getUserConversationById,
  getMessagesByConversationId,
  sendConversationMessage,
  pushSingleMessage,
  updateUserConversationPreferences
};

export default connect(mapStateToProps, mapDispatchToProps)(ConversationContainer);



// WEBPACK FOOTER //
// ./src/components/app/messages/ConversationContainer.js